import classNames from 'classnames';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { FC, MouseEvent } from 'react';
import { groupByArray } from '../../../utils/arrayFunc';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { TeachingBlockCard } from '../TeachingBlockCard/TeachingBlockCard';
import { TeachingBlockCardType } from '../types';
import styles from './CardPicker.module.scss';

export const CardPicker: FC = observer(() => {
  const store = useTeachingBlockStore();

  const getCards = () => {
    if (store.gridContext === 'rooms') {
      return store.placedCards.filter((c) => c.rooms.length === 0);
    }
    return store.selectedRows.length > 0
      ? store.freeCards.filter((card) => {
          return store.selectedRows.some((row) => {
            return store.isUuidPresentInTimetableCard(card, row.value);
          });
        })
      : store.freeCards;
  };

  const cards: TeachingBlockCardType[][] = [];
  const lessonGroups = groupByArray(getCards(), (card) => card.lesson?.uuid);

  lessonGroups.forEach((lessonGroup) => {
    const durationGroups = groupByArray(lessonGroup, (group) => group.duration);
    durationGroups.forEach((durationGroup) => cards.push(durationGroup));
  });

  // sort by subject-name
  const stacks: TeachingBlockCardType[][] = cards.sort((a, b) =>
    (a[0].subject?.name ?? '').localeCompare(b[0].subject?.name ?? ''),
  );

  const onCardClick = (event: MouseEvent<HTMLDivElement>, card: TeachingBlockCardType) => {
    event.stopPropagation();
    event.preventDefault();
    if (!card.locked) {
      store.pickCard(card, { x: event.clientX, y: event.clientY }, true);
    }
  };

  return (
    <div className={classNames(styles['card-picker'], 'has-scrollbar', 'drop-and-delete')}>
      {stacks.map((container) => {
        const card = container[0];
        const cardClasses = classNames({
          [styles.hidden]: container.length === 1 && store.pickedCard?.card === card,
          [styles.multi]: container.length >= 2,
        });

        return (
          <TeachingBlockCard
            className={cardClasses}
            card={{
              ...toJS(card),
              warnings: [],
              availabilityWarnings: [],
            }}
            count={container.length}
            key={card.uuid}
            onClick={onCardClick}
            onHover={(isHovering, card) => {
              if (!isHovering && store.pickedCard) {
                store.selectCard(store.pickedCard.card);
              } else {
                store.selectCard(card);
              }
            }}
            passive={false}
          />
        );
      })}
    </div>
  );
});
